import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Zap, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VISIBLE_MS = 5000;   // how long each toast stays
const GAP_MS = 9000;       // pause between toasts
const FIRST_DELAY_MS = 4000;

const timeAgo = (iso) => {
  if (!iso) return 'just now';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} day(s) ago`;
};

export const SalesToasts = () => {
  const [sales, setSales] = useState([]);
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const idxRef = useRef(0);
  const timers = useRef([]);

  useEffect(() => {
    axios.get(`${API}/recent-sales?limit=10`)
      .then(res => setSales(res.data?.sales || []))
      .catch(() => {});
    // Mount-only fetch of the social-proof feed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dismissed || sales.length === 0) return;
    const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

    const cycle = () => {
      const sale = sales[idxRef.current % sales.length];
      idxRef.current += 1;
      setCurrent(sale);
      setVisible(true);
      timers.current.push(setTimeout(() => setVisible(false), VISIBLE_MS));
      timers.current.push(setTimeout(cycle, VISIBLE_MS + GAP_MS));
    };

    timers.current.push(setTimeout(cycle, FIRST_DELAY_MS));
    return clearTimers;
  }, [sales, dismissed]);

  if (dismissed || !current) return null;

  const isPro = current.tier === 'pro';

  return (
    <div
      className={`fixed left-4 bottom-28 sm:bottom-24 z-40 max-w-[300px] transition-all duration-500 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      data-testid="sales-toast"
      role="status"
      aria-live="polite"
    >
      <div className="relative flex items-start gap-3 rounded-lg border border-green-500/40 bg-black/90 backdrop-blur-md p-3 pr-8 shadow-[0_0_20px_rgba(0,255,0,0.25)]">
        <div className={`shrink-0 mt-0.5 flex items-center justify-center w-9 h-9 rounded-full border ${isPro ? 'border-emerald-400/50 bg-emerald-400/10' : 'border-green-500/40 bg-green-500/10'}`}>
          <Zap className={`w-4 h-4 ${isPro ? 'text-emerald-300' : 'text-green-400'}`} />
        </div>
        <div className="min-w-0">
          <p className="text-green-300 text-sm font-mono truncate" data-testid="sales-toast-buyer">
            {current.buyer}
          </p>
          <p className="text-green-500/80 text-xs">
            just unlocked{' '}
            <span className={`font-bold ${isPro ? 'text-emerald-300' : 'text-green-400'}`}>
              {isPro ? 'PRO' : 'BASIC'}
            </span>
            {' '}· {timeAgo(current.timestamp)}
          </p>
          {current.location && (
            <p className="text-green-500/50 text-xs truncate" data-testid="sales-toast-location">
              📍 {current.location}
            </p>
          )}
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-1.5 right-1.5 text-green-500/50 hover:text-green-300 transition-colors"
          aria-label="Dismiss"
          data-testid="sales-toast-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
